import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { CommonModule } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatTableModule, MatTableDataSource } from "@angular/material/table";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { SolPropertiesLaborRatesAppliedOfficesComponent } from "../../../../dialogs/sol/sol-properties-labor-rates-applied-offices/sol-properties-labor-rates-applied-offices.component";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { MatRadioModule } from "@angular/material/radio";
import { TranslatePipe } from "@ngx-translate/core";
import { ILaborRate } from "../../../../../models/properties/LaborRate";
import { IServicingOffice } from "../../../../../models/properties/ServicingOffice"
import { MultilingualCurrencyPipe} from "../../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import {MatIconModule} from '@angular/material/icon'
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { BusinessAndSalesOffice, BusinessHierarchy, DeploymentOfficeWithLaborRatesResponse, DropDownItem,
     EngineeringOfficesWithLaborRatesResponse, LaborLevelwiseData, LaborRates } from "../../../../../models/dtos";
import { IndexDBService } from "../../../../../services/common/indexDB.service";
import { CommonDataService } from "../../../../../services/common-data.service";
import { take } from "rxjs";
import { CommonMetaDataService } from "../../../../../services/common-meta-data.service";
import { BusinessTypes } from "../../../../../models/dtos";
import { CommonFunctionsService } from "../../../../../services/common-functions.service";


@Component({
    selector: 'app-sol-properties-labor-rates',
    templateUrl: './sol-properties-labor-rates.component.html',
    styleUrls: ['./sol-properties-labor-rates.component.scss'],
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDialogModule,
    MatButtonModule,
    MatRadioModule,
    TranslatePipe,
    MultilingualCurrencyPipe,
    MatIconModule,
]
})
export class SolPropertiesLaborRatesComponent implements OnInit {
    private static readonly OFFERING_SEGMENT_IDS = new Set<number>([21, 22, 25, 26]);

    businessLines: DropDownItem[] = [];
    salesOffices: DropDownItem[] = [];
    segments: DropDownItem[] = [];
    businessHierarchies: BusinessHierarchy[] = [];
    engOffices: EngineeringOfficesWithLaborRatesResponse[] = [];
    deploymentOffices: DeploymentOfficeWithLaborRatesResponse[] = [];

    private allBusinessesAndSalesOffices: BusinessAndSalesOffice[] = [];
    private allBusinessHierarchies: BusinessHierarchy[] = [];
    private salesOfficeLaborRates: LaborRates[] = [];

    laborRates = new MatTableDataSource<ILaborRate>([]);
    engOfficeRates = new MatTableDataSource<IServicingOffice>([]);
    deploymentOfficeRates = new MatTableDataSource<IServicingOffice>([]);

    usePL: boolean = false;
    mechRateProvidedByOther: boolean = false;
    elecRateProvidedByOther: boolean = false;
    isReadOnly: boolean = false;
    private readonly destroyRef = inject(DestroyRef);

    displayedColumns: string[] = ['labor', 'pm', 'eng', 'eeng', 'leng', 'spec', 'mech', 'elec'];

    get displayedEngOfficeColumns(): string[] {
        const selectedOfficeId = this.laborRatesForm.controls.engOffice.value;
        const salesOfficeId = this.laborRatesForm.controls.salesOffice.value;
        return selectedOfficeId !== salesOfficeId
            ? ['labor', 'eng', 'ceng', 'qeng', 'grap', 'prog', 'spec']
            : ['labor', 'eng', 'spec'];
    }

    get displayedDeploymentOfficeColumns(): string[] {
        const selectedOfficeId = this.laborRatesForm.controls.deployment.value;
        const salesOfficeId = this.laborRatesForm.controls.salesOffice.value;
        return selectedOfficeId !== salesOfficeId
            ? ['labor', 'eng', 'neng', 'spec', 'dds', 'elec']
            : ['labor', 'eng', 'dds'];
    }

    laborRatesForm: FormGroup<{
        businessLine: FormControl<number>;
        salesOffice: FormControl<number>;
        segment: FormControl<number>;
        businessHierarchy: FormControl<number>;
        gckCode: FormControl<string>;
        offering: FormControl<boolean>;
        engOffice: FormControl<number>;
        deployment: FormControl<number>;
    }> = new FormGroup({
        businessLine: new FormControl<number>({ value: CONSTANTS.ZERO, disabled: true }),
        salesOffice: new FormControl<number>({ value: CONSTANTS.ZERO, disabled: true }),
        segment: new FormControl<number>({ value: CONSTANTS.ZERO, disabled: true }),
        businessHierarchy: new FormControl<number>({ value: CONSTANTS.ZERO, disabled: true }),
        gckCode: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true }),
        offering: new FormControl<boolean>({ value: true, disabled: true }),
        engOffice: new FormControl<number>({ value: CONSTANTS.ZERO, disabled: true }),
        deployment: new FormControl<number>({ value: CONSTANTS.ZERO, disabled: true }),
    });

    get gckValue(): string {
        return this.laborRatesForm.get('gckCode')?.value ?? '';
    }

    get showUsePLCheckbox(): boolean {
        const businessType = this.commonFunctionsService.getBusinessType(this.commonDataService.getBusiness());
        const showProjectLeadForSecurityEstimate =
            this.commonFunctionsService.getGeneralConfigValue('ShowProjectLeadForSecurityEstimate')?.trim().toLowerCase() === 'true';

        if (
            businessType === BusinessTypes.SERVICE_AGREEMENT ||
            businessType === BusinessTypes.ELECTRICAL ||
            (businessType === BusinessTypes.SECURITY && !showProjectLeadForSecurityEstimate)
        ) {
            return false;
        }

        return true;
    }

    get showEngOfficeWarning(): boolean {
        const salesOffice = this.laborRatesForm.controls.salesOffice.value;
        const engOffice = this.laborRatesForm.controls.engOffice.value;
        return salesOffice != null && engOffice != null && salesOffice === engOffice;
    }

    get showDeploymentWarning(): boolean {
        const salesOffice = this.laborRatesForm.controls.salesOffice.value;
        const deploymentOffice = this.laborRatesForm.controls.deployment.value;
        return salesOffice != null && deploymentOffice != null && salesOffice === deploymentOffice;
    }

    constructor(
        private readonly matDialog: MatDialog,
        private readonly solPropertiesService: SolPropertiesService,
        private readonly indexDbService: IndexDBService,
        private readonly commonDataService: CommonDataService,
        private readonly commonMetaDataService: CommonMetaDataService,
        private readonly commonFunctionsService: CommonFunctionsService
    ) { }

    ngOnInit(): void {
        this.mapAllMasterData();
        this.initializeForm();
        this.subscribeFormValueChanges();
        this.setupReadOnlyMode();
        this.populateLaborRatesTables();
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                this.isReadOnly = isReadOnly;
            });
    }

    private subscribeFormValueChanges(): void {
        this.laborRatesForm.valueChanges
        .pipe(take(CONSTANTS.ONE))
        .subscribe(
            () => this.solPropertiesService.markPropertiesChanged()
        );
    }

    trackById(index: number, item: DropDownItem): number {
        return item.id;
    }

    async appliedOffices(): Promise<void> {
        const laborRatesCollection = await this.indexDbService.getAllLaborData();
        const rates = this.getAllAppliedSalesOffices(laborRatesCollection);
        const filteredRates = this.excludeSelectedOffices(rates);
        const salesOfficeNames = this.buildSalesOfficeNameMap();
        this.matDialog.open(SolPropertiesLaborRatesAppliedOfficesComponent, {
            width: '400px',
            disableClose: true,
            data: { rates: filteredRates, salesOfficeNames }
        });
    }

    private buildSalesOfficeNameMap(): Map<number, string> {
        const nameMap = new Map<number, string>();
        this.allBusinessesAndSalesOffices.forEach(b => {
            nameMap.set(b.id, b.branchName_City);
        });
        this.engOffices.forEach(o => {
            if (!nameMap.has(o.salesofficeId)) {
                nameMap.set(o.salesofficeId, o.salesOfficeName);
            }
        });
        this.deploymentOffices.forEach(o => {
            if (!nameMap.has(o.salesofficeId)) {
                nameMap.set(o.salesofficeId, o.salesOfficeName);
            }
        });
        return nameMap;
    }

    private excludeSelectedOffices(rates: LaborRates[]): LaborRates[] {
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const excludedIds = new Set<number>([
            estimateSettings.salesOfficeId,
            estimateSettings.engOffice,
            estimateSettings.zineOffice
        ]);
        return rates.filter(r => !excludedIds.has(r.salesOfficeId));
    }

    private getAllAppliedSalesOffices(laborRatesCollection: LaborLevelwiseData): LaborRates[] {
        let salesOfficeSet = new Set<number>();
        Object.values(laborRatesCollection.laborsCollection).forEach(element => {
            (element.baseTasksCollection ?? []).forEach(baseTask => {
                salesOfficeSet.add(baseTask.salesOfficeId);
            });
            (element.tasksCollection ?? []).forEach(task => {
                salesOfficeSet.add(task.salesOfficeId);
            });
            (element.conditionsCollection ?? []).forEach(condition => {
                salesOfficeSet.add(condition.salesOfficeId);
            });
        });
        let rates: LaborRates[] = this.getAppliedOfficesRatesBasedOnApplicableSalesOfficeIds([salesOfficeSet]);
        return rates;
    }

    getAppliedOfficesRatesBasedOnApplicableSalesOfficeIds(salesOfficeIds: Set<number>[]): LaborRates[] {
        let rates: LaborRates[] = [];
        this.commonDataService.getLaborMetaData()?.salesOfficeLaborRates?.forEach(element => {
            if(element.salesOfficeId && salesOfficeIds.some(set => set.has(element.salesOfficeId!))){
                element.salesOfficeId && salesOfficeIds.forEach(set => set.delete(element.salesOfficeId!));
                rates.push(element);
            }
        });
        return rates;
    }

    private mapAllMasterData(): void {
        this.allBusinessesAndSalesOffices = this.commonMetaDataService.getMetaData().businessesAndSalesOffices;
        this.segments = this.solPropertiesService.getSegmentsMasterData();
        this.allBusinessHierarchies = this.solPropertiesService.getBusinessHierarchiesMasterData();
        this.engOffices = this.solPropertiesService.getEngineeringOfficesMasterData();
        this.deploymentOffices = this.solPropertiesService.getDeploymentOfficesMasterData();
        this.salesOfficeLaborRates = this.solPropertiesService.getSalesOfficeLaborRates();
        this.deriveBusinessLines();
    }

    private deriveBusinessLines(): void {
        const uniqueMap = new Map<number, DropDownItem>();
        this.allBusinessesAndSalesOffices.forEach(b => {
            if (!uniqueMap.has(b.businessUnitId)) {
                uniqueMap.set(b.businessUnitId, { id: b.businessUnitId, description: b.businessUnitName } as DropDownItem);
            }
        });
        this.businessLines = Array.from(uniqueMap.values());
    }

    private filterSalesOffices(businessUnitId: number): void {
        this.salesOffices = this.allBusinessesAndSalesOffices
            .filter(b => b.businessUnitId === businessUnitId)
            .map(b => ({ id: b.id, description: b.branchName_City } as DropDownItem));
    }

    private filterBusinessHierarchies(segmentId: number): void {
        this.businessHierarchies = this.allBusinessHierarchies
            .filter(bh => bh.segmentId === segmentId)
            .sort((a, b) => a.subSegmentId - b.subSegmentId);
    }

    get showOfferingControls(): boolean {
        return SolPropertiesLaborRatesComponent.OFFERING_SEGMENT_IDS.has(
            this.laborRatesForm.get(CONSTANTS.SEGMENT_LOWERCASE)?.value ?? CONSTANTS.ZERO);
    }

    private initializeForm(): void {
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const estimateData = this.solPropertiesService.getEstimateData();
        const segmentId = estimateData.profitCenterId;

        this.filterSalesOffices(estimateSettings.businessUnitId);
        this.filterBusinessHierarchies(segmentId);

        const savedSubSegmentId = estimateSettings.subSegmentId ?? estimateData.subSegmentID;
        // Match desktop's display fallback of selecting the matching subsegment from the business hierarchy collection if it exists.
        // Or else select the first available subsegment from the business hierarchy collection.
        const selectedHierarchy = this.businessHierarchies.find(bh => bh.subSegmentId === savedSubSegmentId)
            ?? this.businessHierarchies[CONSTANTS.ZERO];

        this.usePL = estimateSettings.usePL;
        this.mechRateProvidedByOther = estimateSettings.mechRateProvidedByOther;
        this.elecRateProvidedByOther = estimateSettings.elecRateProvidedByOther;

        if (!this.showUsePLCheckbox) {
            this.usePL = false;
            estimateSettings.usePL = false;
        }

        this.laborRatesForm.patchValue({
            businessLine: estimateSettings.businessUnitId,
            salesOffice: estimateSettings.salesOfficeId,
            segment: segmentId,
            businessHierarchy: selectedHierarchy?.subSegmentId ?? CONSTANTS.ZERO,
            gckCode: selectedHierarchy?.gckCode ?? CONSTANTS.EMPTY_STRING,
            offering: estimateSettings.fsOffering,
            engOffice: estimateSettings.engOffice,
            deployment: estimateSettings.zineOffice
        });
    }

    private populateLaborRatesTables(): void {
        const estimateSettings = this.solPropertiesService.getEstimateSettings();

        this.populateSalesOfficeLaborRates(estimateSettings.salesOfficeId);
        this.populateEngOfficeLaborRates(estimateSettings.engOffice);
        this.populateDeploymentLaborRates(estimateSettings.zineOffice);
    }

    private findLaborRatesBySalesOfficeId(salesOfficeId: number): LaborRates | undefined {
        return this.salesOfficeLaborRates.find(r => r.salesOfficeId === salesOfficeId);
    }

    private populateSalesOfficeLaborRates(salesOfficeId: number): void {
        const rates = this.findLaborRatesBySalesOfficeId(salesOfficeId);
        if (!rates) {
            this.laborRates.data = [];
            return;
        }

        this.laborRates.data = [
            {
                labor: 'Std Rate',
                pm: rates.projectManagementRates,
                eng: rates.engineeringRates,
                eeng: rates.energyEngineeringRates,
                leng: rates.lEngineeringRates,
                spec: rates.specialistRates,
                mech: rates.mechanicalRates,
                elec: rates.electricianRates
            },
            {
                labor: 'PWR',
                pm: null,
                eng: null,
                eeng: null,
                leng: null,
                spec: rates.specPWRRate,
                mech: rates.mechPWRRate,
                elec: rates.elecPWRRate
            },
            {
                labor: 'Adj PWR',
                pm: null,
                eng: null,
                eeng: null,
                leng: null,
                spec: rates.adjSpecPWRRate,
                mech: rates.adjMechPWRRate,
                elec: rates.adjElecPWRRate
            },
            {
                labor: 'PBO',
                pm: null,
                eng: null,
                eeng: null,
                leng: null,
                spec: null,
                mech: null,
                elec: null
            }
        ];
    }

    private mapLaborRatesToServicingOffice(rates: LaborRates): IServicingOffice {
        return {
            labor: 'Std Rate',
            pm: rates.projectManagementRates,
            eng: rates.engineeringRates,
            ceng: rates.cyberSecEngineeringRates,
            leng: rates.lEngineeringRates,
            neng: rates.networkEngineeringRates,
            qeng: rates.qualificationEngineeringRates,
            grap: rates.graphicsRates,
            prog: rates.programmingRates,
            spec: rates.specialistRates,
            dds: rates.ddsRates,
            mech: rates.mechanicalRates,
            elec: rates.electricianRates
        };
    }

    private populateEngOfficeLaborRates(engOfficeId: number): void {
        const rates = this.findLaborRatesBySalesOfficeId(engOfficeId);
        this.engOfficeRates.data = rates ? [this.mapLaborRatesToServicingOffice(rates)] : [];
    }

    private populateDeploymentLaborRates(deploymentOfficeId: number): void {
        const rates = this.findLaborRatesBySalesOfficeId(deploymentOfficeId);
        this.deploymentOfficeRates.data = rates ? [this.mapLaborRatesToServicingOffice(rates)] : [];
    }

}
